import React, { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Search, UserX, X } from 'lucide-react';
import {
  adminGetStudents, adminGetPlatformStats, adminListInstitutionsBasic, adminGetSchoolGrades, adminUpdateUser,
} from '../../utils/remote';
import type { AdminStudentRow, AdminStudentStatus } from '../../types/admin';
import { Badge, Button, Chip, ConfirmDialog, EmptyState, ErrorPanel, Initials, KpiCard, LoadingState, PagedFooter, PageHeader, PanelCard, PillTabs, Segmented, Table, Td, Th, Track } from '../../school_panel/components/ui';
import { ago } from '../../school_panel/pages/dashboards/sections/format';
import { gradeShort } from './adminUi';
import AccountDevicesDrawer from './AccountDevicesDrawer';
import { downloadCsv, toCsv } from '../../school_panel/lib/csv';
import { fieldClass } from '../../school_panel/lib/fieldClass';

const PAGE_SIZE = 25;
const STALE_DAYS = 30;

const STATUS_OPTIONS: { value: '' | AdminStudentStatus; label: string }[] = [
  { value: '', label: 'Any' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive 30+ days' },
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'free', label: 'Free' },
];

const num = (n: number | undefined) => (n === undefined ? '—' : n.toLocaleString('en-US'));
const studentName = (s: Pick<AdminStudentRow, 'first_name' | 'last_name' | 'username'>) =>
  `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim() || s.username;
const gradeLabel = (s: AdminStudentRow) => [s.grade_name ? gradeShort(s.grade_name) : null, s.section].filter(Boolean).join(' · ') || '—';
const daysSince = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function exportCsv(rows: AdminStudentRow[]) {
  const head = ['id', 'username', 'name', 'school', 'grade', 'section', 'average', 'exams', 'last_exam', 'plan', 'active'];
  downloadCsv('yada-students.csv', toCsv(head, rows.map((s) => [
    s.id, s.username, studentName(s), s.institution_name, s.grade_name, s.section, s.average, s.results_count,
    s.last_result_at, s.subscribed ? 'Subscribed' : 'Free', s.is_active,
  ])));
}

const INPUT = fieldClass;

const AdminStudentsAll: React.FC = () => {
  const qc = useQueryClient();
  const [school, setSchool] = useState<number | null>(null);
  const [grade, setGrade] = useState<number | null>(null);
  const [status, setStatus] = useState<'' | AdminStudentStatus>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<string | null>(null);
  const [devicesFor, setDevicesFor] = useState<number | null>(null);
  const debouncedSearch = useDebounced(search.trim(), 300);
  // While the debounce is pending the query still has the old term, so paging would
  // request page N for a term it is not showing. Paging waits until the term settles.
  const searchPending = search.trim() !== debouncedSearch;

  // A changed view invalidates the selection and the last bulk message. Filter
  // changes also go back to page 1 in the same update, so no request is made for a
  // page that no longer exists under the new filter.
  const changeFilter = (apply: () => void) => { apply(); setPage(1); setSelected(new Set()); setBulkMsg(null); };
  const goToPage = (n: number) => { setPage(n); setSelected(new Set()); setBulkMsg(null); };

  const statsQ = useQuery({ queryKey: ['admin-platform-stats'], queryFn: adminGetPlatformStats, staleTime: 60_000 });
  const schoolsQ = useQuery({ queryKey: ['admin-institutions-basic'], queryFn: adminListInstitutionsBasic, staleTime: 60_000 });
  const gradesQ = useQuery({
    queryKey: ['admin-school-grades', school],
    queryFn: () => adminGetSchoolGrades(school as number),
    enabled: school !== null,
    staleTime: 60_000,
  });

  const params = {
    school: school ?? undefined,
    grade: grade ?? undefined,
    search: debouncedSearch || undefined,
    status: status || undefined,
    sort: 'name' as const,
    page,
    page_size: PAGE_SIZE,
  };
  const studentsQ = useQuery({
    queryKey: ['admin-students', { school, grade, search: debouncedSearch, status, sort: 'name', page }],
    queryFn: () => adminGetStudents(params),
    staleTime: 5_000,
    placeholderData: keepPreviousData,
  });
  const rows = useMemo(() => studentsQ.data?.results ?? [], [studentsQ.data]);
  const count = studentsQ.data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  const pickSchool = (id: number | null) => changeFilter(() => { setSchool(id); setGrade(null); });

  const st = statsQ.data?.students;
  const subtitle = st && statsQ.data
    ? `${num(st.total)} students in ${num(statsQ.data.institutions.total)} schools · ${num(st.active_7d)} took an exam this week · ${num(st.inactive_30d)} with no exam in 30 days.`
    : undefined;
  const pctOf = (n: number) => (st && st.total > 0 ? (n / st.total) * 100 : 0);

  // Only rows that are both selected and currently visible are ever acted on.
  const selRows = rows.filter((r) => selected.has(r.id));
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleOne = (id: number) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const togglePage = () => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r.id)));

  const deactivate = async () => {
    const targets = selRows.map((r) => r.id);
    setBusy(true);
    const res = await Promise.allSettled(targets.map((id) => adminUpdateUser(id, { is_active: false })));
    setBusy(false);
    setConfirmOpen(false);
    const failedIds = targets.filter((_, i) => res[i].status === 'rejected');
    const ok = targets.length - failedIds.length;
    setSelected(new Set(failedIds));
    setBulkMsg(failedIds.length ? `${ok} deactivated · ${failedIds.length} failed` : `${ok} deactivated`);
    qc.invalidateQueries({ queryKey: ['admin-students'] });
    qc.invalidateQueries({ queryKey: ['admin-platform-stats'] });
  };

  const grades = gradesQ.data ?? [];
  const schoolOptions = useMemo(
    () => [...(schoolsQ.data ?? [])].sort((x, y) => x.name.localeCompare(y.name)),
    [schoolsQ.data],
  );

  // Week-over-week change in the active share, in points (needs the previous-week count).
  const activeDelta = st && st.total > 0 && typeof st.active_prev_7d === 'number'
    ? Math.round(((st.active_7d - st.active_prev_7d) / st.total) * 100) : null;
  // Change in the inactive count against 30 days earlier.
  const inactiveDelta = st && typeof st.inactive_prev_30d === 'number' ? st.inactive_30d - st.inactive_prev_30d : null;
  const top = st?.inactive_top_school;
  const inactiveNote = !st ? undefined
    : top && st.inactive_30d > 0
      ? (top.n * 2 === st.inactive_30d ? `Half are at ${top.name}` : top.n * 2 > st.inactive_30d ? `Most are at ${top.name}` : `${num(top.n)} are at ${top.name}`)
      : 'No exam in the last 30 days';

  return (
    <div>
      <PageHeader
        eyebrow="Platform admin · Students"
        title="All students"
        subtitle={subtitle}
        actions={(
          <Button icon={<Download size={16} aria-hidden />} disabled={rows.length === 0} onClick={() => exportCsv(rows)}>Export CSV</Button>
        )}
      />

      <div className="mb-[18px] grid grid-cols-2 gap-[18px] min-[1181px]:grid-cols-4">
        <KpiCard label="Students" value={num(st?.total)} sub={st ? `+${num(st.joined_30d)} in the last 30 days` : undefined} />
        <KpiCard
          label="Active this week"
          chip={activeDelta === null ? undefined : <Chip tone={activeDelta > 0 ? 'up' : activeDelta < 0 ? 'down' : 'neutral'}>{activeDelta > 0 ? '↑ ' : activeDelta < 0 ? '↓ ' : ''}{Math.abs(activeDelta)}%</Chip>}
          value={st ? <>{Math.round(pctOf(st.active_7d))}<span className="text-[15px]">%</span></> : '—'}
          track={st ? <Track value={pctOf(st.active_7d)} label="Students who took an exam in the last 7 days" /> : undefined}
        />
        <KpiCard label="Subscribed" value={num(st?.subscribed)} sub={st ? `${Math.round(pctOf(st.subscribed))}% of students on a paid plan` : undefined} />
        <KpiCard
          label="Inactive 30+ days"
          chip={inactiveDelta === null ? undefined : <Chip tone={inactiveDelta > 0 ? 'down' : inactiveDelta < 0 ? 'up' : 'neutral'}>{inactiveDelta > 0 ? '↑ ' : inactiveDelta < 0 ? '↓ ' : ''}{Math.abs(inactiveDelta)}</Chip>}
          value={num(st?.inactive_30d)} tone="danger" sub={inactiveNote} />
      </div>

      <PanelCard>
        <div className="mb-4 max-w-full">
          <PillTabs
            scroll
            chip
            label="Filter by school"
            value={school === null ? 'all' : String(school)}
            onChange={(k) => pickSchool(k === 'all' ? null : Number(k))}
            options={[
              { key: 'all', label: 'All schools', count: st?.total },
              ...schoolOptions.map((o) => ({ key: String(o.id), label: o.name, count: o.student_count })),
            ]}
          />
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-2.5">
          <label className="relative block max-w-[380px] flex-[1_1_260px]">
            <span className="sr-only">Search students</span>
            <Search size={16} aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
            <input type="search" value={search} onChange={(e) => changeFilter(() => setSearch(e.target.value))} placeholder="Name or username"
              aria-label="Search students" className={`${INPUT} !min-h-[40px] py-0 pl-10`} />
          </label>
          {school !== null && grades.length > 0 && (
            <Segmented
              label="Grade"
              scroll
              value={grade === null ? 'all' : String(grade)}
              onChange={(k) => changeFilter(() => setGrade(k === 'all' ? null : Number(k)))}
              options={[{ key: 'all', label: 'All' }, ...grades.map((g) => ({ key: String(g.id), label: g.name }))]}
            />
          )}
          {school !== null && gradesQ.isError && (
            <p role="status" className="text-[13px] text-danger-ink">Couldn't load grades for this school, so the grade filter is unavailable.</p>
          )}
          <div className="flex min-w-0 max-w-full items-center gap-2 text-[13px] text-muted">
            <span aria-hidden="true">Status</span>
            <Segmented dense scroll label="Status" value={status === '' ? 'any' : status}
              onChange={(k) => changeFilter(() => setStatus(k === 'any' ? '' : (k as AdminStudentStatus)))}
              options={STATUS_OPTIONS.map((o) => ({ key: o.value === '' ? 'any' : o.value, label: o.label }))} />
          </div>
          <span className="ml-auto text-[13px] text-muted">Showing {rows.length} of {count}</span>
        </div>

        {selRows.length > 0 && (
          <div role="region" aria-label="Bulk actions" className="mb-[14px] flex flex-wrap items-center gap-2.5 rounded-[14px] bg-navy py-2.5 pl-4 pr-2.5 text-on-navy">
            <span className="font-semibold">{selRows.length} selected</span>
            <span className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => exportCsv(selRows)}>Export selected</Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirmOpen(true)}>Deactivate</Button>
            <button type="button" aria-label="Clear selection" onClick={() => setSelected(new Set())}
              className="grid h-[34px] w-[34px] place-items-center rounded-[10px] border border-navy-2 bg-navy-2 p-0 text-white hover:bg-navy-3"><X size={16} aria-hidden /></button>
          </div>
        )}
        {bulkMsg && <p role="status" className="mb-3 text-sm text-ink">{bulkMsg}</p>}

        <div>
          {studentsQ.isLoading ? (
            <LoadingState label="Loading students…" />
          ) : studentsQ.isError ? (
            <ErrorPanel title="Failed to load" body="The students could not be loaded." onRetry={() => studentsQ.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState icon={<UserX size={20} />} title="No students match these filters" body="Pick another school or grade." />
          ) : (
            <Table minWidth={1040}>
              <thead>
                <tr>
                  <Th className="w-8">
                    <input type="checkbox" aria-label="Select all on this page" checked={allOnPage} onChange={togglePage} className="h-4 w-4 accent-navy" />
                  </Th>
                  <Th>Student</Th>
                  <Th>School</Th>
                  <Th>Grade</Th>
                  <Th>Average</Th>
                  <Th align="right">Exams</Th>
                  <Th>Last active</Th>
                  <Th>Plan</Th>
                  <Th>Devices</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const name = studentName(s);
                  const on = selected.has(s.id);
                  const stale = s.last_result_at ? daysSince(s.last_result_at) >= STALE_DAYS : false;
                  return (
                    <tr key={s.id} className={on ? '[&>td]:bg-surface-2' : ''}>
                      <Td>
                        <input type="checkbox" aria-label={`Select ${name}`} checked={on} onChange={() => toggleOne(s.id)} className="h-4 w-4 accent-navy" />
                      </Td>
                      <Td>
                        <span className="flex items-center gap-3">
                          <Initials name={name} />
                          <span className="min-w-0">
                            <span className="flex items-center gap-1.5">
                              <span className="font-semibold text-ink">{name}</span>
                              {!s.is_active && <Chip tone="neutral">Inactive</Chip>}
                            </span>
                            <span className="block font-mono text-xs font-medium text-muted">@{s.username}</span>
                          </span>
                        </span>
                      </Td>
                      <Td className="text-ink">{s.institution_name || '—'}</Td>
                      <Td className="whitespace-nowrap font-mono font-medium text-ink">{gradeLabel(s)}</Td>
                      <Td>
                        {s.average === null ? <span className="text-muted">—</span> : (
                          <span className="inline-flex items-center gap-2">
                            <Track className="w-16 flex-none" label={`Average: ${studentName(s)}`} value={s.average} tone={s.average < 50 ? 'accent' : 'slate'} />
                            <span className="font-mono font-semibold text-ink">{s.average.toFixed(1)}%</span>
                          </span>
                        )}
                      </Td>
                      <Td align="right" className="font-mono font-medium text-ink">{s.results_count}</Td>
                      <Td className="whitespace-nowrap">
                        {s.last_result_at
                          ? (stale ? <Badge tone="danger">{ago(s.last_result_at)}</Badge> : <span className="text-muted">{ago(s.last_result_at)}</span>)
                          : <span className="text-muted">—</span>}
                      </Td>
                      <Td><Badge tone={s.subscribed ? 'success' : 'neutral'}>{s.subscribed ? 'Subscribed' : 'Free'}</Badge></Td>
                      <Td><Button size="xs" variant="ghost" aria-label={`Devices for ${name}`} onClick={() => setDevicesFor(s.id)}>Devices</Button></Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>

        {!studentsQ.isLoading && !studentsQ.isError && count > 0 && (
          <fieldset disabled={searchPending} className="m-0 min-w-0 border-0 p-0">
            <PagedFooter divider="list" from={(page - 1) * PAGE_SIZE + 1} to={(page - 1) * PAGE_SIZE + rows.length}
              total={count} page={page} pageCount={totalPages} onPage={goToPage} label="Students pagination" />
          </fieldset>
        )}
      </PanelCard>

      <ConfirmDialog
        isOpen={confirmOpen}
        title={`Deactivate ${selRows.length} ${selRows.length === 1 ? 'student' : 'students'}?`}
        body="They will no longer be able to sign in. You can reactivate them later from Users."
        confirmLabel="Deactivate"
        busy={busy}
        onConfirm={deactivate}
        onCancel={() => setConfirmOpen(false)}
      />
      <AccountDevicesDrawer userId={devicesFor} onClose={() => setDevicesFor(null)} />
    </div>
  );
};

export default AdminStudentsAll;
